import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../contexts/AppContext';
import useSalesOrderService from '../../app/services/hooks/useSalesOrderService';
import { SalesOrder, SalesOrderFilters } from '../../app/models/SalesOrder';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import useNavigationPage from '../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';

import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import SalesOrderDataTable from './components/tables/SalesOrderDataTable';
import SalesOrderCreate from './components/SalesOrderCreate';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Órdenes de Venta',
        url: '/sales-orders',
        isActive: true
    }
];

const SalesOrders = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    const {
        fetchingGetSalesOrders,
        getSalesOrders,
        deleteSalesOrder
    } = useSalesOrderService();

    // Estados
    const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [showingCreate, setShowingCreate] = useState(false);

    // Filtros de la tabla
    const [filter, setFilter] = useState<SalesOrderFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    // Recargar tabla cuando cambian los filtros
    useEffect(() => {
        try {
            getSalesOrders(filter, {
                onSuccess: (response: ServiceResponse) => {
                    setSalesOrders(response.data?.sales_orders || []);
                    setTotalRows(response.data?.total_rows || 0);
                },
                onError: (response: ServiceResponse) => {
                    console.error('Error al cargar órdenes de venta:', response);
                    toast.error(response.message || 'Error al cargar las órdenes de venta', { autoClose: 2000 });
                    setSalesOrders([]);
                    setTotalRows(0);
                    if (response.data?.to_dashboard) {
                        navigationPage('/');
                    }
                }
            });
        } catch (error) {
            console.error('Error en useEffect de SalesOrders:', error);
            toast.error('Error al cargar las órdenes de venta', { autoClose: 2000 });
        }
        // eslint-disable-next-line
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        getSalesOrders(filter, {
            onSuccess: (response: ServiceResponse) => {
                setSalesOrders(response.data?.sales_orders || []);
                setTotalRows(response.data?.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al cargar las órdenes de venta', { autoClose: 2000 });
                setSalesOrders([]);
                setTotalRows(0);
                if (response.data?.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (salesOrderId: number) => {
        navigationPage('/sales-orders/' + salesOrderId + '/edit');
    };


    const destroy = (salesOrderId: number) => {
        const _text = 'Está a punto de eliminar la orden de venta #' + salesOrderId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando orden de venta...');

                        deleteSalesOrder(salesOrderId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La orden de venta no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Órdenes de Venta" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={
                        <ButtonCreate
                            callbackCreate={showCreate}
                            title="Nueva Orden de Venta"
                        />
                    }
                />

                <SalesOrderDataTable
                    salesOrders={salesOrders}
                    totalRows={totalRows}
                    loading={fetchingGetSalesOrders}
                    destroy={destroy}
                    edit={showEdit}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Orden de Venta"
                    backdrop={true}
                    showFooter={false}
                >
                    <SalesOrderCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            )}

        </>
    );
};

export default SalesOrders;

